import type { Melody } from '@tunasong/schemas'
import type { Fingering, StringNumber } from '../guitar/fingering.js'
import { GuitarTunings } from '../guitar/tuning.js'
import { getNoteMIDINumber } from '../note/note-lib.js'
import { Duration } from '../tab/tab.js'
import type { TabStep } from '../tab/tab.js'
import { barBeats, GRID_TOLERANCE } from './grid.js'
import type { MelodyRenderLoss } from './to-score.js'

/**
 * Flatten a melody into `TabStep`s for an ADDITIVE tab sibling (§3).
 *
 * Pitch -> (string, fret) picks the HIGHEST string that can reach the note without going below
 * the open string, which is what a player reads most easily for a single line. Notes below the
 * low E or past fret 24 cannot be written and are reported, not silently dropped.
 *
 * `TabStep` has no rests and its `Duration` enum is w/h/q/8/16, so the same losses as the staff
 * apply: offsets dropped, durations rounded, rests not written.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §3
 */

const MAX_FRET = 24

const STRING_NUMBERS: StringNumber[] = [1, 2, 3, 4, 5, 6]

const STRING_MIDI = STRING_NUMBERS.map(str => ({ str, midi: getNoteMIDINumber(GuitarTunings.normal[str]) }))

const fingeringFor = (midi: number): Fingering | null => {
  const best = STRING_MIDI.find(s => midi >= s.midi && midi - s.midi <= MAX_FRET)
  return best ? { str: best.str, fret: midi - best.midi } : null
}

/**
 * SHORTEST first, so `reduce`'s strict `<` keeps the earlier entry on an exact tie and the tie
 * resolves SHORT - the same rule `convert.ts` applies. A step written too long overruns the next
 * step; written short it never does.
 */
const DURATION_STEPS: { beats: number; duration: Duration }[] = [
  { beats: 0.25, duration: Duration.SixteenthNote },
  { beats: 0.5, duration: Duration.EightsNote },
  { beats: 1, duration: Duration.QuarterNote },
  { beats: 2, duration: Duration.HalfNote },
  { beats: 4, duration: Duration.WholeNote },
]

const nearestDuration = (beats: number): { duration: Duration; exact: boolean } => {
  const best = DURATION_STEPS.reduce((carry, entry) =>
    Math.abs(entry.beats - beats) < Math.abs(carry.beats - beats) ? entry : carry
  )
  return { duration: best.duration, exact: Math.abs(best.beats - beats) <= GRID_TOLERANCE }
}

export const melodyToTabSteps = (melody: Melody): { steps: TabStep[]; losses: MelodyRenderLoss[] } => {
  const losses: MelodyRenderLoss[] = []
  const perBar = barBeats(melody.tempo.meter)
  const steps: TabStep[] = []

  if (melody.notes.some(n => Math.round(Math.abs(n.offsetMs)) > 0)) {
    losses.push({
      kind: 'offsets-dropped',
      message: 'The tab cannot show the timing offsets - the melody keeps them.',
    })
  }

  melody.notes.forEach((note, index) => {
    const fingering = fingeringFor(note.midi)
    if (!fingering) {
      losses.push({
        kind: 'out-of-range',
        noteIndex: index,
        message: `Note ${index + 1}: MIDI ${note.midi} is outside the guitar range and is not written.`,
      })
      return
    }
    const { duration, exact } = nearestDuration(note.duration)
    if (!exact) {
      losses.push({
        kind: 'duration-rounded',
        noteIndex: index,
        message: `Note ${index + 1}: ${note.duration} beats written as ${duration}.`,
      })
    }
    const previous = melody.notes[index - 1]
    if (previous && note.onset - (previous.onset + previous.duration) > GRID_TOLERANCE) {
      losses.push({
        kind: 'rest-dropped',
        noteIndex: index,
        message: `Note ${index + 1}: the rest before it is not written.`,
      })
    }
    steps.push({
      fingering,
      duration,
      startsMeasure: Math.abs(note.onset % perBar) <= GRID_TOLERANCE,
    })
  })

  return { steps, losses }
}
